import { Chart as ChartJS, registerables } from 'chart.js'
import { FC } from 'react'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { useFormatCurrency } from '../../lib/CurrencyFormatter'

ChartJS.register(...registerables)

export type LineChartSeries = {
  label: string
  color: string
  data: number[]
  tooltipCallback?: (dataIndex: number) => string | string[]
}

type Props = {
  heading?: string
  data?: { [key: string]: string | number }
  budget?: number
  tooltipCallback?: (dataIndex: number) => string | string[]
  labels?: string[]
  series?: LineChartSeries[]
  showLegend?: boolean
}

const LineChart: FC<Props> = ({
  heading,
  data,
  budget,
  tooltipCallback,
  labels,
  series,
  showLegend,
}) => {
  const { t } = useTranslation('common')
  const formatCurrency = useFormatCurrency()

  const buildSingleSeriesData = () => {
    const chartData = structuredClone(data!)
    const secondChartData = {} as { [key: string]: number }

    // If only one item in the data object exists duplicate this one item
    // to make the line chart visible.
    if (Object.keys(data!).length === 1) {
      chartData[t('common:charts.now')] = data![Object.keys(data!)[0]]
    }

    if (budget !== undefined) {
      Object.keys(chartData).forEach((key) => {
        secondChartData[key] = budget
      })
    }

    return {
      labels: Object.keys(chartData),
      datasets: [
        {
          label: heading,
          data: Object.values(chartData),
          fill: false,
          borderColor: 'rgb(69, 123, 157)',
          tension: 0.1,
        },
        {
          label: t('common:charts.secondLine'),
          data: Object.values(secondChartData),
          fill: false,
          borderColor: 'rgb(220, 20, 60)',
          tension: 0.1,
          pointRadius: 0,
          borderWidth: 1,
        },
      ],
    }
  }

  const buildMultiSeriesData = () => {
    // Same trick as the single-series case: a lone point won't render as
    // a visible line, so duplicate it onto a "now" point.
    const singlePoint = labels!.length === 1
    const chartLabels = singlePoint ? [...labels!, t('common:charts.now')] : labels!

    return {
      labels: chartLabels,
      datasets: series!.map((line) => ({
        label: line.label,
        data: singlePoint ? [...line.data, line.data[0]] : line.data,
        fill: false,
        borderColor: line.color,
        tension: 0.1,
      })),
    }
  }

  return (
    <Line
      data={series && labels ? buildMultiSeriesData() : buildSingleSeriesData()}
      options={{
        plugins: {
          legend: {
            display: showLegend ?? (series ? series.length > 1 : !!heading),
          },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: function (context) {
                return context[0].label
              },
              label: function (context) {
                const seriesTooltipCallback =
                  series?.[context.datasetIndex]?.tooltipCallback

                if (tooltipCallback) {
                  return tooltipCallback(context.dataIndex)
                }

                if (seriesTooltipCallback) {
                  return seriesTooltipCallback(context.dataIndex)
                }

                return `${context.dataset.label ? `${context.dataset.label}: ` : ''}${formatCurrency(context.parsed.y, false)}`
              },
            },
          },
        },
      }}
    />
  )
}

export default LineChart
